import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import { CalendarPlus, Clock, MapPin } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MESES = ["ENE", "FEB", "MAR", "ABR", "MAY", "JUN", "JUL", "AGO", "SEP", "OCT", "NOV", "DIC"];

const inputCls =
  "w-full border-b border-white/20 bg-transparent py-3 text-sm font-light tracking-wide text-paper placeholder:text-paper/40 transition-colors duration-300 focus:border-paper";

const Eventos = () => {
  const [events, setEvents] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [form, setForm] = useState({ nombre: "", email: "", telefono: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/events`)
      .then(({ data }) => setEvents(data))
      .catch(() => setEvents([]));
  }, []);

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = events.filter((e) => e.fecha >= today);

  const register = async (e, eventId) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/events/${eventId}/register`, form);
      toast.success("¡Registro recibido! Te esperamos.");
      setForm({ nombre: "", email: "", telefono: "" });
      setOpenId(null);
    } catch {
      toast.error("No pudimos registrar tu asistencia. Inténtalo de nuevo.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="eventos" className="border-t border-white/10 bg-ink py-28 lg:py-36" data-testid="eventos-section">
      <div className="mx-auto max-w-[1600px] px-6 lg:px-12">
        <div className="mb-16 grid gap-8 lg:grid-cols-2">
          <Reveal>
            <p className="mb-8 flex items-center gap-4 text-[10px] font-light uppercase tracking-[0.5em] text-fire" data-testid="eventos-eyebrow">
              <span className="inline-block h-px w-16 bg-fire" /> Agenda
            </p>
            <h2 className="font-serif text-5xl leading-[0.95] tracking-tight text-paper sm:text-7xl" data-testid="eventos-title">
              Campañas, vigilias y <em className="italic text-gold">eventos</em>
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="flex items-end">
            <p className="max-w-md text-sm font-light leading-relaxed tracking-wide text-smoke">
              Regístrate a los próximos eventos especiales de nuestra congregación. Aparta la fecha y ven con tu familia.
            </p>
          </Reveal>
        </div>

        {upcoming.length === 0 ? (
          <Reveal>
            <div className="border border-dashed border-white/15 py-24" data-testid="eventos-empty">
              <p className="text-center font-serif text-3xl italic text-smoke">Muy pronto anunciaremos nuevos eventos</p>
              <p className="mt-4 text-center text-[10px] font-light uppercase tracking-[0.35em] text-paper/30">
                Mientras tanto, te esperamos cada domingo de 10:00 AM a 12:00 PM
              </p>
            </div>
          </Reveal>
        ) : (
          <div data-testid="eventos-list">
            {upcoming.map((ev, i) => {
              const [y, m, d] = ev.fecha.split("-");
              const open = openId === ev.id;
              return (
                <Reveal key={ev.id} delay={i * 0.08}>
                  <article
                    className="group border-t border-white/10 py-10 transition-colors duration-500 last:border-b hover:bg-surface lg:px-6"
                    data-testid={`evento-card-${ev.id}`}
                  >
                    <div className="grid items-start gap-8 lg:grid-cols-[auto_1fr_auto]">
                      <div className="flex items-baseline gap-3">
                        <span className="font-serif text-7xl leading-none text-paper">{d}</span>
                        <span className="text-[10px] font-light uppercase tracking-[0.3em] text-fire">
                          {MESES[parseInt(m, 10) - 1]}<br />{y}
                        </span>
                      </div>
                      <div>
                        <h3 className="font-serif text-3xl text-paper transition-colors duration-300 group-hover:text-fire lg:text-4xl">{ev.titulo}</h3>
                        <div className="mt-3 flex flex-wrap gap-6 text-[10px] font-light uppercase tracking-[0.25em] text-smoke">
                          {ev.hora && (
                            <span className="flex items-center gap-2">
                              <Clock size={14} weight="light" className="text-fire" /> {ev.hora}
                            </span>
                          )}
                          {ev.lugar && (
                            <span className="flex items-center gap-2">
                              <MapPin size={14} weight="light" className="text-fire" /> {ev.lugar}
                            </span>
                          )}
                        </div>
                        {ev.descripcion && (
                          <p className="mt-4 max-w-2xl text-sm font-light leading-relaxed tracking-wide text-smoke">{ev.descripcion}</p>
                        )}
                      </div>
                      {!open && (
                        <button
                          onClick={() => setOpenId(ev.id)}
                          data-testid={`evento-register-btn-${ev.id}`}
                          className="inline-flex items-center gap-2 border border-paper/30 px-7 py-3 text-[10px] font-medium uppercase tracking-[0.3em] text-paper transition-colors duration-300 hover:border-paper hover:bg-paper hover:text-ink"
                        >
                          <CalendarPlus size={14} weight="light" /> Registrar
                        </button>
                      )}
                    </div>

                    {open && (
                      <form onSubmit={(e) => register(e, ev.id)} className="mt-8 max-w-xl space-y-6 lg:pl-6" data-testid={`evento-form-${ev.id}`}>
                        <input required value={form.nombre} onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))} placeholder="Tu nombre *" className={inputCls} data-testid={`evento-input-nombre-${ev.id}`} />
                        <div className="grid gap-6 sm:grid-cols-2">
                          <input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="Correo electrónico" className={inputCls} data-testid={`evento-input-email-${ev.id}`} />
                          <input value={form.telefono} onChange={(e) => setForm((f) => ({ ...f, telefono: e.target.value }))} placeholder="Teléfono / WhatsApp" className={inputCls} data-testid={`evento-input-telefono-${ev.id}`} />
                        </div>
                        <div className="flex gap-3 pt-2">
                          <button type="submit" disabled={sending} data-testid={`evento-submit-${ev.id}`} className="bg-fire px-7 py-3 text-[10px] font-medium uppercase tracking-[0.3em] text-ink transition-colors duration-300 hover:bg-paper disabled:opacity-60">
                            {sending ? "Enviando..." : "Confirmar registro"}
                          </button>
                          <button type="button" onClick={() => setOpenId(null)} data-testid={`evento-cancel-${ev.id}`} className="border border-white/20 px-7 py-3 text-[10px] font-light uppercase tracking-[0.3em] text-smoke transition-colors duration-300 hover:border-paper hover:text-paper">
                            Cancelar
                          </button>
                        </div>
                      </form>
                    )}
                  </article>
                </Reveal>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default Eventos;
